import { useCallback, useMemo, useState } from 'react';
import type { AccommodationCard } from '../Accommodations.types';

type UseManageModeReturn = Readonly<{
  isManageMode: boolean;
  selectedIds: ReadonlySet<string>;
  pendingOrder: ReadonlyArray<AccommodationCard> | null;
  enterManageMode: () => void;
  exitManageMode: () => void;
  toggleSelection: (id: string) => void;
  selectAll: (ids: ReadonlyArray<string>) => void;
  clearSelection: () => void;
  setPendingOrder: (items: ReadonlyArray<AccommodationCard>) => void;
  reconcileAfterDelete: (deletedIds: ReadonlyArray<string>) => void;
}>;

export const useManageMode = (): UseManageModeReturn => {
  const [isManageMode, setIsManageMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingOrder, setPendingOrderState] = useState<ReadonlyArray<AccommodationCard> | null>(null);

  const enterManageMode = useCallback(() => setIsManageMode(true), []);

  const exitManageMode = useCallback(() => {
    setIsManageMode(false);
    setSelectedIds(new Set());
    setPendingOrderState(null);
  }, []);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: ReadonlyArray<string>) => setSelectedIds(new Set(ids)), []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const setPendingOrder = useCallback((items: ReadonlyArray<AccommodationCard>) => setPendingOrderState(items), []);

  const reconcileAfterDelete = useCallback((deletedIds: ReadonlyArray<string>) => {
    const deleted = new Set(deletedIds);
    setSelectedIds(prev => {
      const next = new Set(prev);
      deletedIds.forEach(id => next.delete(id));
      return next;
    });
    setPendingOrderState(prev => (prev ? prev.filter(item => !deleted.has(item.id)) : prev));
  }, []);

  return useMemo(
    () => ({
      isManageMode,
      selectedIds,
      pendingOrder,
      enterManageMode,
      exitManageMode,
      toggleSelection,
      selectAll,
      clearSelection,
      setPendingOrder,
      reconcileAfterDelete,
    }),
    [isManageMode, selectedIds, pendingOrder, enterManageMode, exitManageMode, toggleSelection, selectAll, clearSelection, setPendingOrder, reconcileAfterDelete],
  );
};
